import type { BaseAddress, MailingAddress } from '~/types/ecommerce';

interface FormatMoneyOptions {
  /** Thousands separator (e.g., ",") */
  separator?: string;
  /** Decimal separator (e.g., ".") */
  decimalPoint?: string;
  /** Number of decimal places */
  decimals?: number;
  /** Currency symbol (e.g., "$") */
  symbol?: string;
  /** Whether to place the symbol after the number (default is before) */
  append?: boolean;
  /** Whether to keep leading zeros (default true) */
  leadZeros?: boolean;
}

const defaults: FormatMoneyOptions = {
  separator: ',',
  decimalPoint: '.',
  decimals: 2,
  symbol: '$',
  append: false,
  leadZeros: true,
};

/**
 * Format a numeric value as a currency string.
 *
 * @param value - The number to format
 * @param options - Currency formatting options (optional)
 * @returns A formatted string with symbol and separators, or `undefined` if value is not numeric
 *
 * @example
 * formatCurrency(1234.56) // "$1,234.56"
 * formatCurrency(-50, { symbol: '€' }) // "€-50.00"
 */
const formatCurrency = (
  value: number,
  options: FormatMoneyOptions = defaults,
): string | undefined => {
  if (!Number.isFinite(value)) {
    return undefined;
  }

  const { separator, decimalPoint, decimals, symbol, append, leadZeros } = {
    ...defaults,
    ...options,
  } as Required<FormatMoneyOptions>;

  const absValue = Math.abs(value);
  let formatted = absValue.toFixed(decimals);

  if (!leadZeros) {
    formatted = String(parseFloat(formatted));
  }

  const resultArr = formatted.split('.');
  const left: string = resultArr[0] ?? '0';
  const right: string = resultArr.length > 1 ? decimalPoint + resultArr[1] : '';

  const formattedLeft = left.replace(/\B(?=(\d{3})+(?!\d))/g, separator);

  return `${value < 0 ? '-' : ''}${
    append ? formattedLeft + right + symbol : symbol + formattedLeft + right
  }`;
};

/**
 * Format a number as a percentage string with no decimals.
 *
 * @param value - Numeric value
 * @returns Percentage string (e.g., "25%")
 */
const formatPercent = (value: number): string => `${value.toFixed()}%`;

/**
 * Create a pluralized string for a noun based on count.
 *
 * @param count - The numeric count
 * @param noun - The noun to pluralize
 * @param suffix - The suffix to append for plural (default: "s")
 * @returns String with count and noun, pluralized if count ≠ 1 or 0
 *
 * @example
 * pluralize(1, "item") // "1 item"
 * pluralize(3, "item") // "3 items"
 */
const pluralize = (count: number, noun: string, suffix = 's') =>
  `${count} ${noun}${(count !== 1 && count !== 0) ? suffix : ''}`;

/**
 * Format a numeric rating to 2 decimal places, trimming trailing zero.
 *
 * @param value - Numeric rating
 * @returns Formatted rating string
 *
 * @example
 * formatRating(4.5) // "4.5"
 * formatRating(4.00) // "4"
 */
const formatRating = (value: number) => value.toFixed(2).replace(/0{1}$/, '');

/**
 * Format a phone number string to standard US format.
 *
 * @param phoneNumber - Raw phone number string
 * @returns Formatted phone number (e.g., "(555) 123-4567")
 */
const formatPhoneNumber = (phoneNumber: string) =>
  phoneNumber
    .replace(/\D/g, '')
    .replace(
      /(\d*)(\d{3})(\d{3})(\d{4})$/,
      (s, a, b, c, d) => `+${a} (${b}) ${c}-${d}`,
    )
    .replace(/\+(1\b|\s)\s*/, '');

/**
 * Format a BaseAddress object into a single line string.
 *
 * @param address - Address object (ref or value)
 * @returns A formatted address string
 */
const formatAddress = (address: MailingAddress | BaseAddress) =>
  join(
    concat(
      ', ',
      address.street,
      address.street2,
      address.city,
      address.state,
    ),
    address.postcode,
  );

/**
 * Concatenate non-empty parts using a separator.
 *
 * @param separator - Separator string
 * @param parts - Strings to join
 * @returns Concatenated string
 */
const concat = (separator: string, ...parts: (string | null | undefined)[]) =>
  parts
    .filter(p => !!p?.length)
    .join(separator)
    .trim();

/**
 * Join non-empty parts with a single space.
 *
 * @param parts - Strings to join
 * @returns Concatenated string
 */
const join = (...parts: (string | null | undefined)[]) => concat(' ', ...parts);

/**
 * Mask all but the last `last` characters of a string.
 *
 * @param value - Original string
 * @param last - Number of characters to keep unmasked (default: 4)
 * @param char - Mask character (default: "X")
 * @returns Masked string
 *
 * @example
 * maskify("1234567890123456") // "XXXXXXXXXXXX3456"
 */
const maskify = (value: string, last = 4, char = 'X') =>
  value.slice(0, last * -1).replace(/./g, char) + value.slice(last * -1);

/**
 * Convert a block of text into HTML paragraphs.
 *
 * @param text - Raw text (may include line breaks)
 * @returns String of HTML paragraphs with <br> for single line breaks
 */
export const paragraphize = (text?: string | null) => {
  if (!text?.trim()?.length) {
    return '';
  }

  return text
    .trim()
    .replace(/\r\n/g, '\n') // Normalize CRLF to LF
    .split(/\n\s*\n+/) // Split into paragraphs, collapsing excessive blank lines
    .filter(Boolean) // Remove empty elements to prevent empty <p> tags
    .map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`) // Convert single line breaks to <br>
    .join('');
};

/**
 * Provides common formatting utilities for currency, percent, rating, phone, address, etc.
 *
 * @returns An object with formatting methods
 */
export const useFormatting = () => {
  return {
    currency: formatCurrency,
    percent: formatPercent,
    rating: formatRating,
    phone: formatPhoneNumber,
    address: formatAddress,
    maskify,
    pluralize,
    paragraphize,
    concat,
    join,
  };
};
